import { createSearchParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useMediaQuery, Checkbox, FormControlLabel, Badge } from '@mui/material'
import { ArrowBack, Close, Search, Upload } from '@mui/icons-material'
import InputAdornment from '@mui/material/InputAdornment'
import OutlinedInput from '@mui/material/OutlinedInput'
import { styled, useTheme } from '@mui/material/styles'
import React, { useEffect, useState } from 'react'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import { useTranslation } from 'react-i18next'
import Tooltip from '@mui/material/Tooltip'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'

import useLogin from 'shared/store/useLogin'
import { UserRoles } from 'shared/consts'
import TireIcon from 'assets/images/tire/tire.png'

import ContainedButton from '../Buttons/ContainedButton'
import OutlinedButton from '../Buttons/OutlinedButton'
import OilModal from './components/OilModal/index'

export const SearchbarStyle = styled('div')(() => ({
   display: 'flex',
   alignItems: 'center',
}))

export const StyledInput = styled('input')({
   width: '50px',
   border: 'none',
   backgroundColor: 'rgba(0,0,0,0)',
   '&:hover': {
      cursor: 'pointer',
      fontWeight: 'bold',
   },
})

export default function Searchbar() {
   const navigate = useNavigate()
   const [openOilModal, setOpenOilModal] = useState(false)
   const { user } = useLogin()
   const theme = useTheme()
   const [isLoading, setIsLoading] = useState<boolean>(false)
   const [searchParams] = useSearchParams()
   const location = useLocation()
   const [notFoundIndexes, setNotFoundIndexes] = useState<string[]>()
   const [activeIndex, setActiveIndex] = useState(0)
   const searchParam = searchParams.get('value') || ''
   const replacementsSearch = searchParams.get('index') || ''
   const [searchValue, setSearchValue] = useState('')
   const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
   const [oeSearch, setOeSearch] = useState(false)

   const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

   useEffect(() => {
      const searchType = searchParams.get('searchType')
      if (searchType === 'SUBSTITUTES') {
         setSearchValue(replacementsSearch)
      } else {
         setSearchValue(searchParam)
      }
   }, [])
   const { t } = useTranslation('TRANSLATION')

   function handleOnSearch() {
      const clearedValue = searchValue.replace(/[#,!$%^&*;:{}=_<>@|\\[\]`~£§()'"?\\]/g, '')
      const searchType = oeSearch ? 'OE' : 'INDEX'

      if (
         (user.roles.includes(UserRoles.INTERNAL) || user.roles.includes(UserRoles.DATA_MANAGER)) &&
         uuidRegex.test(searchValue)
      ) {
         navigate(`/dashboard/product?uuid=${searchValue}`)
      } else if (searchValue && clearedValue.length > 1) {
         if (user.roles.includes(UserRoles.PZU)) {
            navigate({
               pathname: '/dashboard/erp-product',
               search: `?${createSearchParams({
                  value: clearedValue,
               })}`,
            })
         } else {
            navigate({
               pathname: '/dashboard/search',
               search: `?${createSearchParams({
                  value: clearedValue,
                  searchType,
               })}`,
            })
         }
      }
   }

   function renderNotFoundIndexes() {
      if (notFoundIndexes) {
         return (
            <Box sx={{ display: 'flex', gap: 1, ml: 2, alignItems: 'center' }}>
               <ContainedButton
                  onClick={() => {
                     setActiveIndex((prevState) => prevState - 1)
                  }}
                  disabled={activeIndex === 0 || isLoading}
                  variant="contained"
               >
                  <ArrowBack />
               </ContainedButton>
               <Typography variant="body1">{`${activeIndex + 1}/${notFoundIndexes.length}`}</Typography>
               <ContainedButton
                  onClick={() => {
                     setActiveIndex((prevState) => prevState + 1)
                  }}
                  disabled={activeIndex === notFoundIndexes.length - 1 || isLoading}
                  variant="contained"
               >
                  <ArrowBack sx={{ transform: 'rotate(180deg)' }} />
               </ContainedButton>
               <IconButton onClick={() => setNotFoundIndexes(undefined)}>
                  <Close />
               </IconButton>
            </Box>
         )
      }
      return null
   }

   useEffect(() => {
      if (!location.pathname.includes('search') && !location.pathname.includes('product')) {
         setSearchValue('')
      }
   }, [location.pathname])

   useEffect(() => {
      if (location.state) {
         if (location.state.indexes) {
            setNotFoundIndexes(location.state.indexes)
         }
      } else setNotFoundIndexes(undefined)
   }, [location])

   useEffect(() => {
      if (location.state) {
         if (location.state.indexes) {
            setActiveIndex(location.state.activeIndex || 0)
            setSearchValue(location.state.indexes[activeIndex])
         }
      }
   }, [navigate])

   useEffect(() => {
      setIsLoading(true)
      setTimeout(() => setIsLoading(false), 1500)
      if (notFoundIndexes) {
         navigate(
            {
               pathname: '/dashboard/search',
               search: `?${createSearchParams({
                  value: notFoundIndexes[activeIndex],
                  searchType: 'INDEX',
               })}`,
            },
            { state: { indexes: notFoundIndexes, activeIndex } }
         )
         setSearchValue(notFoundIndexes[activeIndex])
      }
   }, [activeIndex])

   return (
      <SearchbarStyle>
         <FormControlLabel
            control={<Checkbox value={oeSearch} onChange={(_, checked) => setOeSearch(checked)} />}
            label="OE"
         />
         <OutlinedInput
            sx={{
               transition: 'width .5s',
               pr: 0,
               [theme.breakpoints.up('md')]: {
                  width: '300px',
                  '&.Mui-focused': {
                     width: '400px',
                  },
               },
               [theme.breakpoints.down('md')]: {
                  width: '160px',
               },
            }}
            disabled={!!localStorage.getItem('searchBlocked')}
            size="small"
            value={searchValue}
            onFocus={(e) => {
               e.target.select()
            }}
            onKeyDown={(e) => {
               if (e.key === 'Enter') {
                  handleOnSearch()
               }
            }}
            onChange={(e) => setSearchValue(e.target.value)}
            autoFocus={!isMobile}
            fullWidth
            inputProps={{
               maxLength:
                  user.roles.includes(UserRoles.DATA_MANAGER) || user.roles.includes(UserRoles.INTERNAL) ? 40 : 30,
            }}
            placeholder={
               isMobile ? t('components.searchProduct.placeholderShort') : t('components.searchProduct.placeholder')
            }
            startAdornment={
               <InputAdornment sx={{ display: { lg: 'flex', md: 'flex', xs: 'none' } }} position="start">
                  <Box component={Search} sx={{ color: 'text.disabled', width: 15, height: 15 }} />
               </InputAdornment>
            }
            color="primary"
            endAdornment={
               <Button
                  onClick={handleOnSearch}
                  disabled={!searchValue || searchValue.replace(/[#,!$%^&*;:{}=_<>@|\\[\]`~£§()'"?\\]/g, '').length < 2}
                  variant="contained"
                  sx={{ height: 35 }}
               >
                  <Box component={Search} sx={{ color: 'white', width: 20, height: 20 }} />
               </Button>
            }
         />

         {/* {user.roles.includes(UserRoles.INTERNAL) && !isMobile && (
            <Badge badgeContent="BETA" color="error">
               <Button
                  onClick={() => {
                     if (!location.pathname.includes('tires-search')) {
                        navigate('/dashboard/tires-search?noFetch=true')
                     }
                  }}
                  sx={{ ml: 2 }}
                  variant="contained"
               >
                  <img src={TireIcon} style={{ filter: 'invert(1)', width: 25, height: 'auto' }} alt="oil" />
               </Button>
            </Badge>
         )} */}
         {/* <Badge badgeContent="BETA" color="error">
            <Button onClick={() => setOpenOilModal(true)} sx={{ ml: 2 }} variant="contained">
               <img src={GetOilLogo()} style={{ width: 25, height: 'auto' }} alt="oil" />
            </Button>
         </Badge> */}
         <Box sx={{ display: { lg: 'flex', md: 'none', xs: 'none' }, gap: 2, ml: 2, maxWidth: 320 }}>
            {!user.roles.includes(UserRoles.PRO) &&
            !user.roles.includes(UserRoles.ULTRA) &&
            !user.roles.includes(UserRoles.NITRO) &&
            !user.roles.includes(UserRoles.TURBO) ? (
               <Tooltip title={t('components.csvSearch.disabledTooltip') || ''}>
                  <OutlinedButton sx={{ display: 'flex', alignItems: 'center' }} disabled variant="outlined">
                     {t('components.csvSearch.uploadCsv')} <Upload color="disabled" sx={{ fontSize: 20, mb: -0.5 }} />
                  </OutlinedButton>
               </Tooltip>
            ) : (
               <OutlinedButton
                  sx={{ display: 'flex', alignItems: 'center' }}
                  variant="outlined"
                  onClick={() => navigate('/dashboard/import-cart')}
               >
                  {t('components.csvSearch.uploadCsv')} <Upload sx={{ fontSize: 20, mb: 0.5 }} />
               </OutlinedButton>
            )}
         </Box>
         {(user.roles.includes(UserRoles.PRO) ||
            user.roles.includes(UserRoles.ULTRA) ||
            user.roles.includes(UserRoles.NITRO) ||
            user.roles.includes(UserRoles.TURBO)) &&
            renderNotFoundIndexes()}

         <OilModal open={openOilModal} handleClose={() => setOpenOilModal(false)} />
      </SearchbarStyle>
   )
}
